// Plugins: user code that runs in a sandboxed iframe (allow-scripts only: null origin, no cookies,
// no access to the app) and talks to the document through a small message API. Every call that
// edits is one undoable step, attributed to the plugin; view-only files refuse edits as usual.

import { createFromSpec, nodeToSpec, specToProps, resolveType, describeTree, makeNode, newId, instantiate, booleanOperation, flatten, toSvg, exportBounds, Renderer2D, type NodeData, type NodeId, type NodeSpec, type Op, type VariableValue } from '@vellum/core';
import { app } from './app.ts';
import { timerOf, timerAction } from './timer.ts';
import { importSvg } from './svgImport.ts';
import { libraryFamilies } from './customFonts.ts';
import { pageFlows, linkPreviewSpec, isLink, type LinkPreview, componentSetOf, variantOptions, setVariant, swapInstance, combineAsVariants } from '@vellum/core';
import { FIGMA_SHIM } from './figmaShim.ts';
import { applyFigmaOps, type FigmaOp } from './figmaOps.ts';

export interface PluginParameter {
  key: string;
  name: string;
  description?: string;
  allowFreeform?: boolean;
  optional?: boolean;
}

export interface PluginDef {
  id: string;
  name: string;
  /** JavaScript run with `vellum` in scope (async API), or Figma plugin code using `figma.*`. */
  code: string;
  /** For Figma-style plugins: the UI HTML (available as __html__). */
  ui?: string;
  builtIn?: boolean;
  /** Quick-action parameters (Figma's manifest "parameters"): asked for before the plugin runs. */
  parameters?: PluginParameter[];
  /** Set once published to (or installed from) the community directory. */
  communityId?: string;
}

const KEY = 'vellum-plugins';

export const BUILT_IN: PluginDef[] = [
  {
    id: 'figma-rectangles',
    name: 'Rectangle creator (Figma plugin API)',
    builtIn: true,
    // the starter template Figma generates for new plugins, unchanged
    code: `const numberOfRectangles = 5;
const nodes = [];
for (let i = 0; i < numberOfRectangles; i++) {
  const rect = figma.createRectangle();
  rect.x = i * 150;
  rect.fills = [{ type: 'SOLID', color: { r: 1, g: 0.5, b: 0 } }];
  figma.currentPage.appendChild(rect);
  nodes.push(rect);
}
figma.currentPage.selection = nodes;
figma.viewport.scrollAndZoomIntoView(nodes);
figma.closePlugin();`,
  },
  {
    id: 'rename-by-content',
    name: 'Rename layers by their text',
    builtIn: true,
    code: `// Names text layers and stickies after what they say.
const sel = await vellum.getSelection();
const ids = sel.length ? sel.map((n) => n.id) : (await vellum.find({})).filter((n) => n.text).map((n) => n.id);
const nodes = await vellum.getNodes(ids, { deep: true });
const updates = nodes.filter((n) => typeof n.text === 'string' && n.text.trim()).map((n) => ({ id: n.id, name: n.text.trim().slice(0, 40) }));
await vellum.updateNodes(updates);
vellum.notify('Renamed ' + updates.length + ' layer(s)');
vellum.close();`,
  },
  {
    id: 'palette',
    name: 'Color palette (with UI)',
    builtIn: true,
    code: `// Shows a small UI; clicking a swatch fills the selection.
const colors = ['#0d99ff', '#9747ff', '#f24822', '#ffcd29', '#14ae5c', '#1e1e1e'];
const swatches = colors.map((c) => '<button data-c="' + c + '" style="background:' + c + '"></button>').join('');
vellum.showUI(
  '<style>body{font:12px sans-serif;margin:10px}button{width:44px;height:44px;border:0;border-radius:8px;margin:3px;cursor:pointer}</style>' +
    swatches +
    '<script>document.onclick = (e) => e.target.dataset.c && parent.postMessage({ pluginMessage: e.target.dataset.c }, "*");<\\/script>',
  { width: 180, height: 130, title: 'Palette' },
);
vellum.onMessage(async (color) => {
  const sel = await vellum.getSelection();
  if (!sel.length) return vellum.notify('Select layers to color');
  await vellum.updateNodes(sel.map((n) => ({ id: n.id, fill: color })));
});`,
  },
];

export function installedPlugins(): PluginDef[] {
  let mine: PluginDef[] = [];
  try {
    mine = JSON.parse(localStorage.getItem(KEY) ?? '[]');
  } catch {
    /* storage unavailable */
  }
  return [...BUILT_IN, ...mine];
}

export function savePlugin(p: PluginDef) {
  const mine = installedPlugins().filter((x) => !x.builtIn && x.id !== p.id);
  try {
    localStorage.setItem(KEY, JSON.stringify([...mine, p]));
  } catch {
    app.toast('Could not save the plugin (browser storage is unavailable)');
  }
}

export function removePlugin(id: string) {
  try {
    localStorage.setItem(KEY, JSON.stringify(installedPlugins().filter((x) => !x.builtIn && x.id !== id)));
  } catch {
    /* ignore */
  }
}

// The script inside the sandbox: an async `vellum` API over postMessage.
const BOOT = `
const pending = new Map(); let seq = 0; let onMsg = null;
const call = (method, ...args) => new Promise((resolve, reject) => { const id = ++seq; pending.set(id, { resolve, reject }); parent.postMessage({ __vellum: 'call', id, method, args }, '*'); });
addEventListener('message', (e) => {
  const m = e.data || {};
  if (m.__vellum === 'reply') { const p = pending.get(m.id); if (!p) return; pending.delete(m.id); m.error ? p.reject(new Error(m.error)) : p.resolve(m.value); }
  if (m.__vellum === 'ui' && onMsg) onMsg(m.message);
});
const vellum = {
  getSelection: () => call('getSelection'),
  getNodes: (ids, opts) => call('getNodes', ids, opts),
  find: (query) => call('find', query),
  tree: () => call('tree'),
  createNodes: (specs, parentId) => call('createNodes', specs, parentId),
  updateNodes: (updates) => call('updateNodes', updates),
  deleteNodes: (ids) => call('deleteNodes', ids),
  select: (ids) => call('select', ids),
  notify: (text) => call('notify', String(text)),
  showUI: (html, opts) => call('showUI', String(html), opts || {}),
  postMessage: (message) => call('toUI', message),
  onMessage: (fn) => { onMsg = fn; },
  close: () => call('close'),
};
`;

let running: { def: PluginDef; frame: HTMLIFrameElement; ui: HTMLIFrameElement | null; panel: HTMLDivElement | null; listening: Set<string>; cleanup: (() => void)[]; onSuggestions?: (key: string, list: { name: string; data: unknown }[], message?: string) => void } | null = null;

/** Send a message to the running plugin's sandbox (parameters, run). */
export function postToPlugin(msg: Record<string, unknown>) {
  running?.frame.contentWindow?.postMessage(msg, '*');
}

/** Where the running plugin's parameter suggestions go. */
export function onPluginSuggestions(fn: (key: string, list: { name: string; data: unknown }[], message?: string) => void) {
  if (running) running.onSuggestions = fn;
}

/** Run a plugin; `command` is what figma.command reads (relaunch buttons pass theirs). */
export function runPlugin(def: PluginDef, command = '') {
  stopPlugin();
  const frame = document.createElement('iframe');
  // allow-scripts without allow-same-origin: an opaque origin with no access to the app or its cookies
  frame.setAttribute('sandbox', 'allow-scripts');
  frame.style.display = 'none';
  const figmaApi = /\bfigma\./.test(def.code);
  // Figma-API plugins run against a local copy of the page; their changes flush as one batch
  const pre = figmaApi ? `${FIGMA_SHIM}\nconst __html__ = ${JSON.stringify(def.ui ?? '')};\n__load(await call('figmaSnapshot'));\nfigma.command = ${JSON.stringify(command)};\n` : '';
  const post = figmaApi ? '\nawait __flush();' : '';
  const body = `${pre}${def.code}${post}`.replace(/<\/script/gi, '<\\/script');
  frame.srcdoc = `<!doctype html><script>${BOOT}\n(async () => { try {\n${body}\n} catch (e) { parent.postMessage({ __vellum: 'call', id: 0, method: 'error', args: [String(e && e.message || e)] }, '*'); } })();<\/script>`;
  document.body.appendChild(frame);
  running = { def, frame, ui: null, panel: null, listening: new Set(), cleanup: [] };
  app.toast(`Running “${def.name}”`);
}

// figma.teamLibrary and import*ByKeyAsync, on top of shared libraries (keys are "<file>/<id>")
type LibItem = { id: string; name: string; type: string; variables?: { id: string; name: string; resolvedType?: string }[] };
async function libraryOf(file: string): Promise<LibItem[]> {
  const r = await fetch(`/api/files/${file}/library`);
  return r.ok ? ((await r.json()) as { items: LibItem[] }).items : [];
}
async function teamLibraryCollections() {
  const files = (await (await fetch('/api/files')).json()) as { id: string; name: string }[];
  const out: { key: string; name: string; libraryName: string }[] = [];
  for (const f of files) if (f.id !== app.fileId) for (const it of await libraryOf(f.id)) if (it.type === 'VARIABLE_COLLECTION') out.push({ key: `${f.id}/${it.id}`, name: it.name, libraryName: f.name });
  return out;
}
async function teamLibraryVariables(key: string) {
  const [file, coll] = key.split('/');
  const c = (await libraryOf(file)).find((it) => it.id === coll);
  return (c?.variables ?? []).map((v) => ({ key: `${file}/${v.id}`, name: v.name, resolvedType: v.resolvedType }));
}
/** figma.createLinkPreviewAsync: a link card in the current page, filled from the page it links to. */
async function linkPreviewCard(url: string): Promise<{ id: string } | { error: string }> {
  if (!isLink(url)) return { error: 'createLinkPreviewAsync needs an http(s) URL' };
  let meta: LinkPreview = { url };
  const r = await fetch('/api/unfurl', { method: 'POST', body: JSON.stringify({ url }), headers: { 'content-type': 'application/json' } }).catch(() => null);
  if (r?.ok) meta = (await r.json()) as LinkPreview;
  const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  const res = createFromSpec(app.editor, linkPreviewSpec(meta, { x: Math.round(c.x - 180), y: Math.round(c.y - 140) }));
  return { id: res.ids[0] };
}

async function teamLibraryImport(key: string, variable: boolean): Promise<{ id: string } | { error: string }> {
  const [file, id] = key.split('/');
  if (!file || !id) return { error: `Bad library key ${key}` };
  // a variable comes in with its whole collection
  const itemId = variable ? (await libraryOf(file)).find((it) => it.variables?.some((v) => v.id === id))?.id : id;
  if (!itemId) return { error: `Nothing in that library has the key ${key}` };
  const r = await fetch(`/api/files/${app.fileId}/library`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ source: file, itemId }) });
  const body = (await r.json()) as { localId?: string; error?: string };
  if (!r.ok || !body.localId) return { error: body.error ?? 'Import failed' };
  const local = variable ? `${file}~${id}` : body.localId;
  // the copy arrives through sync: wait for it
  for (let i = 0; i < 60 && !app.doc.has(local); i++) await new Promise((res) => setTimeout(res, 50));
  return { id: local };
}

/** Text review: a plugin checks the text being edited; its suggestions underline ranges. */
export interface TextReviewRange {
  start: number;
  end: number;
  suggestions: string[];
  color: string;
}
export const textReview = {
  enabled: false,
  frame: null as HTMLIFrameElement | null,
  results: new Map<NodeId, { text: string; ranges: TextReviewRange[] }>(),
  /** Ask the plugin about this text (the answer arrives as textreviewResult). */
  check(id: NodeId, text: string) {
    if (this.enabled) this.frame?.contentWindow?.postMessage({ __vellum: 'textreview', id, text }, '*');
  },
};

function viewportInfo() {
  const r = app.viewportRect();
  return { center: { x: r.x + r.width / 2, y: r.y + r.height / 2 }, zoom: app.camera.zoom, bounds: r };
}

/** Fonts plugins can use: the built-in families and the server's font library. */
function listFonts() {
  const styles = ['Regular', 'Italic', 'Medium', 'Semi Bold', 'Bold', 'Bold Italic'];
  const families = ['Inter', 'system-ui', 'Georgia', 'Times New Roman', 'Helvetica', 'Arial', 'Courier New', 'Menlo', 'Verdana', 'Trebuchet MS', ...libraryFamilies()];
  return [...new Set(families)].flatMap((family) => styles.map((style) => ({ fontName: { family, style } })));
}

interface ExportSettings {
  format?: 'PNG' | 'JPG' | 'SVG' | 'SVG_STRING' | 'PDF' | 'JSON_REST_V1';
  constraint?: { type: 'SCALE' | 'WIDTH' | 'HEIGHT'; value: number };
  /** SVG: text as outlines (Figma's default) or as <text> */
  svgOutlineText?: boolean;
}

/** figma.exportAsync: bytes (or an SVG string) of a layer, rendered from the real file. */
async function exportNode(id: NodeId, s: ExportSettings): Promise<number[] | string | { error: string }> {
  const doc = app.doc;
  if (!doc.has(id)) return { error: `Node ${id} not found` };
  const format = s.format ?? 'PNG';
  const svgOpts = { outlineText: s.svgOutlineText !== false, shaderImage: (n: NodeId, k: 'fill' | 'effect', i: number) => app.shaders.dataUrl(n, k, i) };
  if (format === 'SVG_STRING') return toSvg(doc, [id], svgOpts);
  if (format === 'SVG') return Array.from(new TextEncoder().encode(toSvg(doc, [id], svgOpts)));
  if (format === 'JSON_REST_V1') return { error: 'JSON_REST_V1 export is not supported; read the node instead' };
  if (format === 'PDF') {
    const r = await fetch(`/api/files/${app.fileId}/pdf?page=${doc.pageOf(id)?.id ?? ''}&ids=${id}`);
    if (!r.ok) return { error: 'PDF export failed' };
    return Array.from(new Uint8Array(await r.arrayBuffer()));
  }
  const b = exportBounds(doc, [id]);
  if (!b) return { error: 'Nothing to export' };
  const c = s.constraint;
  const scale = !c ? 1 : c.type === 'SCALE' ? c.value : c.type === 'WIDTH' ? c.value / b.width : c.value / b.height;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(b.width * scale));
  canvas.height = Math.max(1, Math.round(b.height * scale));
  const ctx = canvas.getContext('2d')!;
  if (format === 'JPG') {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  new Renderer2D(doc).renderSubtree(ctx, id, [scale, 0, 0, scale, -b.x * scale, -b.y * scale], { images: app.images, shaders: app.shaders });
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, format === 'JPG' ? 'image/jpeg' : 'image/png', 0.92));
  return blob ? Array.from(new Uint8Array(await blob.arrayBuffer())) : { error: 'Export failed' };
}

export function stopPlugin() {
  if (!running) return;
  app.setCodegen(null);
  if (textReview.frame === running.frame) (textReview.enabled = false), (textReview.frame = null), textReview.results.clear();
  for (const off of running.cleanup) off();
  running.frame.remove();
  running.panel?.remove();
  running = null;
}

function reply(target: Window, id: number, value?: unknown, error?: string) {
  target.postMessage({ __vellum: 'reply', id, value, error }, '*');
}

/** Run an editing call as one undoable step named after the plugin. */
function edit<T>(fn: () => T): T {
  const ed = app.editor;
  let out!: T;
  ed.transact(`Plugin: ${running?.def.name ?? 'plugin'}`, () => {
    out = fn();
  });
  return out;
}

function handle(method: string, args: unknown[]): unknown {
  const ed = app.editor;
  const doc = app.doc;
  const spec = (id: NodeId, deep?: boolean) => ({ id, ...(nodeToSpec(doc, id, deep ? 8 : 0) as object) });
  switch (method) {
    case 'getSelection':
      return ed.selection.filter((id) => doc.has(id)).map((id) => spec(id));
    case 'getNodes': {
      const [ids, opts] = args as [NodeId[], { deep?: boolean } | undefined];
      const out: unknown[] = [];
      for (const id of ids ?? []) {
        if (!doc.has(id)) continue;
        if (opts?.deep) doc.walk(id, (n) => void out.push(spec(n.id)));
        else out.push(spec(id));
      }
      return out;
    }
    case 'find': {
      const q = (args[0] ?? {}) as { name?: string; type?: string; text?: string };
      const type = q.type ? resolveType(q.type) : undefined;
      const out: unknown[] = [];
      doc.walk(ed.pageId, (n) => {
        if (n.id === ed.pageId) return;
        if (type && n.type !== type) return;
        if (q.name && !n.name.toLowerCase().includes(q.name.toLowerCase())) return;
        if (q.text && !(n.characters ?? '').toLowerCase().includes(q.text.toLowerCase())) return;
        out.push({ id: n.id, type: n.type, name: n.name, text: n.characters });
      });
      return out.slice(0, 2000);
    }
    case 'tree':
      return describeTree(doc, ed.pageId, 6);
    case 'createNodes': {
      const [specs, parentId] = args as [NodeSpec[], NodeId | undefined];
      return edit(() => createFromSpec(ed, specs, parentId && doc.has(parentId) ? parentId : undefined).ids);
    }
    case 'updateNodes': {
      const updates = (args[0] ?? []) as ({ id: NodeId } & Partial<NodeSpec>)[];
      return edit(() => {
        let n = 0;
        for (const { id, ...rest } of updates) {
          if (!doc.has(id)) continue;
          const t = doc.must(id).type;
          const props = specToProps({ type: t, ...rest } as NodeSpec, t);
          // text "fill" is the text color
          if ('fill' in rest && t === 'TEXT') props.fills = specToProps({ type: 'TEXT', fill: rest.fill } as NodeSpec, 'TEXT').fills;
          ed.update(id, props, 'Plugin edit');
          n++;
        }
        return n;
      });
    }
    case 'deleteNodes':
      return edit(() => (ed.remove(((args[0] ?? []) as NodeId[]).filter((id) => doc.has(id))), true));
    case 'select':
      ed.setSelection(((args[0] ?? []) as NodeId[]).filter((id) => doc.has(id)));
      return true;
    case 'notify':
      app.toast(String(args[0]).slice(0, 200));
      return true;
    case 'showUI':
      showUI(String(args[0]), (args[1] ?? {}) as { width?: number; height?: number; title?: string });
      return true;
    case 'toUI':
      running?.ui?.contentWindow?.postMessage({ pluginMessage: args[0] }, '*');
      return true;
    case 'close':
      setTimeout(stopPlugin, 0);
      return true;
    case 'figmaSnapshot': {
      const nodes: NodeData[] = [];
      doc.walk(ed.pageId, (n) => {
        if (n.id !== ed.pageId && nodes.length < 20000) nodes.push(n);
      });
      // components on other pages (and imported library components) can be instantiated too
      for (const p of doc.pages()) {
        if (p.id === ed.pageId) continue;
        for (const c of doc.children(p.id)) if ((c.type === 'COMPONENT' || c.isComponentSet) && nodes.length < 20000) doc.walk(c.id, (n) => void nodes.push(n));
      }
      const collections = doc.children(doc.rootId).filter((n) => n.type === 'VARIABLE_COLLECTION');
      const vars = { collections: collections.map((c) => ({ id: c.id, name: c.name, modes: c.modes, defaultModeId: c.defaultModeId })), variables: collections.flatMap((c) => doc.children(c.id).map((v) => ({ id: v.id, name: v.name, parent: c.id, resolvedType: v.resolvedType, valuesByMode: v.valuesByMode, scopes: v.scopes, codeSyntax: v.codeSyntax }))) };
      const styles = doc.children(doc.rootId).filter((n) => n.type === 'STYLE').map((n) => ({ id: n.id, name: n.name, styleType: n.styleType, fills: n.fills, effects: n.effects, fontSize: n.fontSize, fontFamily: n.fontFamily, fontWeight: n.fontWeight, italic: n.italic, lineHeight: n.lineHeight, layoutGrids: n.layoutGrids }));
      return { pluginId: running?.def.id, fileId: app.fileId, currentUser: { id: app.sync?.clientId ?? 'me', name: app.userName, photoUrl: null, color: '#0d99ff', sessionId: 0 }, activeUsers: [{ id: app.sync?.clientId ?? 'me', name: app.userName, photoUrl: null, color: '#0d99ff', sessionId: 0 }, ...[...app.peers.values()].filter((p) => p.kind === 'human').map((p, i) => ({ id: p.clientId, name: p.name, photoUrl: null, color: p.color, sessionId: i + 1 }))], viewport: viewportInfo(), textreviewEnabled: textReview.enabled, timer: { ...timerOf(doc.get(ed.pageId)), at: Date.now() }, pageId: ed.pageId, pageName: doc.get(ed.pageId)?.name, flows: pageFlows(doc, ed.pageId), fileName: doc.get(doc.rootId)?.name, pages: doc.pages().map((p) => ({ id: p.id, name: p.name })), selection: ed.selection.filter((id) => doc.has(id)), nodes, vars, styles };
    }
    case 'figmaFlush': {
      const [ops, sel, images] = args as [FigmaOp[], NodeId[] | null, Record<string, number[] | string> | undefined];
      return (async () => {
        // images first: bytes become uploaded assets, URLs are used as they are
        const urls = new Map<string, string>();
        for (const [hash, v] of Object.entries(images ?? {})) urls.set(hash, typeof v === 'string' ? v : await uploadImage(new Uint8Array(v)));
        const idMap: Record<string, string> = {};
        edit(() => applyFigmaOps(ed, ops, urls, idMap, { importSvg: (svg) => importSvg(app, svg, { x: 0, y: 0 }), toast: (m) => app.toast(m) }));
        const final = (sel ?? []).map((id) => idMap[id] ?? id).filter((id) => doc.has(id));
        if (sel) ed.setSelection(final);
        if (final.length) app.zoomToNodes(final);
        return { idMap };
      })();
    }
    case 'figmaSetPage': {
      const id = String(args[0]);
      if (doc.get(id)?.type === 'PAGE') ed.setPage(id);
      return handle('figmaSnapshot', []);
    }
    case 'figmaListen': {
      const name = String(args[0]);
      if (!running || running.listening.has(name)) return true;
      running.listening.add(name);
      const frame = running.frame;
      const send = () => frame.contentWindow?.postMessage({ __vellum: 'event', name }, '*');
      let page = ed.pageId;
      const off =
        name === 'selectionchange'
          ? app.on('selection', send)
          : name === 'currentpagechange'
            ? app.on('doc', () => {
                if (app.editor.pageId !== page) (page = app.editor.pageId), send();
              })
            : null;
      if (off) running.cleanup.push(off);
      return true;
    }
    case 'figmaExport':
      return exportNode(String(args[0]), (args[1] ?? {}) as ExportSettings);
    case 'listFonts':
      return listFonts();
    case 'openExternal': {
      // only web links, and only after the person agrees
      const url = String(args[0]);
      if (/^https?:\/\//.test(url) && confirm(`“${running?.def.name}” wants to open ${url}`)) window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    }
    case 'saveVersion':
      return fetch(`/api/files/${app.fileId}/versions`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ label: String(args[0]) || `Saved by ${running?.def.name}` }) })
        .then((r) => r.json())
        .then((v: { id?: string }) => ({ id: v.id ?? '' }));
    case 'textreviewEnable':
      textReview.enabled = !!args[0] && confirm(`Let “${running?.def.name}” check the text you type?`);
      textReview.frame = textReview.enabled ? (running?.frame ?? null) : null;
      app.emit('ui');
      return textReview.enabled;
    case 'textreviewResult':
      textReview.results.set(String(args[0]), { text: String(args[1]), ranges: args[2] as TextReviewRange[] });
      app.emit('ui');
      return true;
    case 'timer':
      timerAction(args[0] as 'start' | 'pause' | 'resume' | 'stop', Number(args[1] ?? 0));
      return { ...timerOf(doc.get(ed.pageId)), at: Date.now() };
    case 'teamLibraryCollections':
      return teamLibraryCollections();
    case 'teamLibraryVariables':
      return teamLibraryVariables(String(args[0]));
    case 'teamLibraryImport':
      return teamLibraryImport(String(args[0]), !!args[1]);
    case 'linkPreview':
      return linkPreviewCard(String(args[0]));
    case 'paramSuggestions':
      running?.onSuggestions?.(String(args[0]), (args[1] as { name: string; data: unknown }[]) ?? [], args[2] as string | undefined);
      return true;
    case 'codegenRegister':
      if (running) app.setCodegen({ name: running.def.name, frame: running.frame });
      return true;
    case 'codegenUnregister':
      app.setCodegen(null);
      return true;
    case 'codegenRefresh':
      app.emit('selection');
      return true;
    case 'codegenResult':
      app.codegenResult(String(args[0]), args[1] as { title: string; language: string; code: string }[]);
      return true;
    case 'storageGet':
    case 'storageSet':
    case 'storageDelete':
    case 'storageKeys': {
      // figma.clientStorage: per plugin, in this browser
      const key = `vellum-plugin-storage:${running?.def.id}`;
      let store: Record<string, unknown> = {};
      try {
        store = JSON.parse(localStorage.getItem(key) ?? '{}');
      } catch {
        /* unavailable */
      }
      if (method === 'storageGet') return store[String(args[0])];
      if (method === 'storageKeys') return Object.keys(store);
      if (method === 'storageSet') store[String(args[0])] = args[1];
      else delete store[String(args[0])];
      try {
        localStorage.setItem(key, JSON.stringify(store));
      } catch {
        throw new Error('Storage is unavailable');
      }
      return undefined;
    }
    case 'error':
      app.toast(`Plugin error: ${String(args[0]).slice(0, 160)}`);
      setTimeout(stopPlugin, 0);
      return true;
    default:
      throw new Error(`Unknown plugin API ${method}`);
  }
}

async function uploadImage(bytes: Uint8Array): Promise<string> {
  const type = bytes[0] === 0x89 ? 'image/png' : bytes[0] === 0xff ? 'image/jpeg' : bytes[0] === 0x47 ? 'image/gif' : bytes[0] === 0x52 ? 'image/webp' : 'image/png';
  const r = await fetch('/api/assets', { method: 'POST', headers: { 'content-type': type }, body: new Blob([bytes as BlobPart], { type }) });
  if (!r.ok) throw new Error('Could not upload the image');
  return ((await r.json()) as { url: string }).url;
}

function showUI(html: string, opts: { width?: number; height?: number; title?: string }) {
  if (!running) return;
  running.panel?.remove();
  const panel = document.createElement('div');
  panel.className = 'plugin-panel';
  const head = document.createElement('header');
  head.textContent = opts.title ?? running.def.name;
  const close = document.createElement('button');
  close.textContent = '✕';
  close.onclick = stopPlugin;
  head.appendChild(close);
  const ui = document.createElement('iframe');
  ui.setAttribute('sandbox', 'allow-scripts');
  ui.style.width = `${Math.min(480, opts.width ?? 280)}px`;
  ui.style.height = `${Math.min(640, opts.height ?? 320)}px`;
  ui.srcdoc = `<!doctype html><meta charset="utf-8">${html}`;
  panel.append(head, ui);
  document.body.appendChild(panel);
  running.ui = ui;
  running.panel = panel;
}

/** Listen for plugin calls (only from the running plugin's own frames). */
export function installPluginHost() {
  window.addEventListener('message', (e) => {
    if (!running) return;
    const m = e.data as { __vellum?: string; id?: number; method?: string; args?: unknown[]; pluginMessage?: unknown };
    // messages from the plugin's UI go to the plugin code
    if (running.ui && e.source === running.ui.contentWindow && m && 'pluginMessage' in m) {
      running.frame.contentWindow?.postMessage({ __vellum: 'ui', message: m.pluginMessage }, '*');
      return;
    }
    if (e.source !== running.frame.contentWindow || m?.__vellum !== 'call') return;
    const src = e.source as Window;
    try {
      Promise.resolve(handle(String(m.method), m.args ?? [])).then(
        (v) => reply(src, m.id!, v),
        (err) => reply(src, m.id!, undefined, (err as Error).message),
      );
    } catch (err) {
      reply(src, m.id!, undefined, (err as Error).message);
    }
  });
}
